// Shared image cache for all components v3.0
// This ensures BoxThumbnail and OfflineImage use the same cache
import logger from './logger';

logger.debug('ImageCache', '🚀 v5.3 - two-tier cache system initialized');

// Simple in-memory cache for loaded images (URLs only)
export const loadedImageCache = new Set();

// Blob cache for actual image data
export const imageBlobCache = new Map();

// Cache for images that successfully loaded but couldn't create blobs (CORS issues, etc.)
export const originalUrlCache = new Set();

// Cache for images currently being loaded (prevents duplicate downloads)
export const loadingImageCache = new Set();

// Callbacks for images being loaded (so waiting components get notified)
const loadingCallbacks = new Map();

// Normalize URL for caching by removing query parameters
export const normalizeUrlForCache = (url) => {
  if (!url) return url;
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.pathname; // Only use the path part, ignore query params
  } catch (e) {
    // If URL parsing fails, just remove query string manually
    return url.split('?')[0];
  }
};

// Utility functions for cache management (exported for debugging and direct usage)
export const clearImageCache = () => {
  loadedImageCache.clear();
  logger.debug('ImageCache', 'Image cache cleared');
};

export const clearOriginalUrlCache = () => {
  originalUrlCache.clear();
  logger.debug('ImageCache', 'Original URL cache cleared');
};

export const clearBlobCache = () => {
  // Revoke any existing blob URLs first to prevent memory leaks
  imageBlobCache.forEach((blobUrl) => {
    try {
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      logger.warn('ImageCache', 'Error revoking blob URL:', e);
    }
  });
  imageBlobCache.clear();
  logger.debug('ImageCache', 'Blob cache cleared and URLs revoked');
};

// Selectively clear cache entries related to a specific item or URL pattern
export const clearCacheForPattern = (pattern) => {
  if (!pattern) return;
  
  // Track how many entries we clear
  let clearedCount = 0;
  
  // Check and clear entries from loadedImageCache
  const loadedEntriesToRemove = [];
  loadedImageCache.forEach(url => {
    if (url.includes(pattern)) {
      loadedEntriesToRemove.push(url);
    }
  });
  loadedEntriesToRemove.forEach(url => {
    loadedImageCache.delete(url);
    clearedCount++;
  });
  
  // Check and clear entries from originalUrlCache
  const originalUrlEntriesToRemove = [];
  originalUrlCache.forEach(url => {
    if (url.includes(pattern)) {
      originalUrlEntriesToRemove.push(url);
    }
  });
  originalUrlEntriesToRemove.forEach(url => {
    originalUrlCache.delete(url);
    clearedCount++;
  });
  
  // Check and clear entries from imageBlobCache
  const blobKeysToRemove = [];
  imageBlobCache.forEach((blobUrl, key) => {
    if (key.includes(pattern)) {
      blobKeysToRemove.push(key);
    }
  });
  blobKeysToRemove.forEach(key => {
    // Revoke the blob URL to prevent memory leaks
    try {
      const blobUrl = imageBlobCache.get(key);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    } catch (e) {
      logger.warn('ImageCache', 'Error revoking blob URL for key:', key, e);
    }
    imageBlobCache.delete(key);
    clearedCount++;
  });
  
  logger.debug('ImageCache', `Cleared ${clearedCount} cache entries matching pattern: ${pattern}`);
  return clearedCount;
};

// Clear cache entries for a specific box
export const clearCacheForBox = (boxId) => {
  if (!boxId) return 0;
  return clearCacheForPattern(`/box/${boxId}/`);
};

// Clear cache entries for a specific item
export const clearCacheForItem = (itemId) => {
  if (!itemId) return 0;
  return clearCacheForPattern(`/item/${itemId}/`);
};

// Clear all caches in one call
export const clearAllImageCaches = () => {
  clearImageCache();
  clearOriginalUrlCache();
  clearBlobCache();
  loadingImageCache.clear();
  loadingCallbacks.clear();
  logger.debug('ImageCache', 'All image caches completely cleared');
};

export const getCacheSize = () => loadedImageCache.size;

export const getCachedImages = () => Array.from(loadedImageCache);

// Add image to cache
export const addToCache = (url) => {
  const normalizedUrl = normalizeUrlForCache(url);
  loadedImageCache.add(normalizedUrl);
  logger.debug('ImageCache', `✅ ADDED TO CACHE: ${normalizedUrl} (original: ${url})`);
  return normalizedUrl;
};

// Check if image is in cache
export const isInCache = (url) => {
  const normalizedUrl = normalizeUrlForCache(url);
  return loadedImageCache.has(normalizedUrl);
};

// Check if image is currently being loaded
export const isLoading = (url) => {
  const normalizedUrl = normalizeUrlForCache(url);
  return loadingImageCache.has(normalizedUrl);
};

// Mark image as loading
export const markAsLoading = (url) => {
  const normalizedUrl = normalizeUrlForCache(url);
  loadingImageCache.add(normalizedUrl);
  logger.debug('ImageCache', `🔄 MARKED AS LOADING: ${normalizedUrl}`);
  return normalizedUrl;
};

// Mark image as finished loading (success or failure)
export const markAsFinished = (url, success = true, error = null) => {
  const normalizedUrl = normalizeUrlForCache(url);
  loadingImageCache.delete(normalizedUrl);
  
  // Notify all waiting callbacks
  const callbacks = loadingCallbacks.get(normalizedUrl) || [];
  callbacks.forEach(callback => {
    try {
      callback(success, error);
    } catch (e) {
      logger.error('ImageCache', 'Error in callback:', e);
    }
  });
  loadingCallbacks.delete(normalizedUrl);
  
  logger.debug('ImageCache', `✅ FINISHED LOADING: ${normalizedUrl} (notified ${callbacks.length} callbacks)`);
  return normalizedUrl;
};

// Add callback for when image finishes loading
export const addLoadingCallback = (url, callback) => {
  const normalizedUrl = normalizeUrlForCache(url);
  if (!loadingCallbacks.has(normalizedUrl)) {
    loadingCallbacks.set(normalizedUrl, []);
  }
  loadingCallbacks.get(normalizedUrl).push(callback);
  logger.debug('ImageCache', `📞 ADDED CALLBACK for ${normalizedUrl}`);
};

// Get cached blob URL for image
export const getCachedBlobUrl = (url) => {
  const normalizedUrl = normalizeUrlForCache(url);
  return imageBlobCache.get(normalizedUrl);
};

// Add image to original URL cache (for images that can't create blobs)
export const addToOriginalUrlCache = (url) => {
  const normalizedUrl = normalizeUrlForCache(url);
  originalUrlCache.add(normalizedUrl);
  logger.debug('ImageCache', `✅ ADDED TO ORIGINAL URL CACHE: ${normalizedUrl}`);
  return normalizedUrl;
};

// Check if image is in original URL cache
export const isInOriginalUrlCache = (url) => {
  const normalizedUrl = normalizeUrlForCache(url);
  return originalUrlCache.has(normalizedUrl);
};

// Check if URL is external (different origin)
const isExternalUrl = (url) => {
  try {
    // Data URLs are not external
    if (url.startsWith('data:')) {
      return false;
    }
    // If URL starts with / or is relative, it's not external
    if (url.startsWith('/') || !url.includes('://')) {
      return false;
    }
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin !== window.location.origin;
  } catch (e) {
    return false;
  }
};

// Add image blob to cache
export const addBlobToCache = async (url, imageElement) => {
  const normalizedUrl = normalizeUrlForCache(url);
  
  // Debug logging
  logger.debug('ImageCache', `🔍 addBlobToCache: url="${url}", normalized="${normalizedUrl}", isExternal=${isExternalUrl(url)}`);
  // Note: We no longer skip external URLs here. With proper CORS headers
  // (Access-Control-Allow-Origin) and images requested with
  // crossOrigin="anonymous", canvas operations are permitted and we can
  // safely generate a blob. If CORS is not configured, the canvas call
  // will throw and we'll gracefully skip caching via the catch below.
  
  try {
    // Create canvas and draw image to get blob
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = imageElement.naturalWidth;
    canvas.height = imageElement.naturalHeight;
    
    // Draw image to canvas - this might fail if image is tainted
    ctx.drawImage(imageElement, 0, 0);
    
    // Convert to blob and create blob URL
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            imageBlobCache.set(normalizedUrl, blobUrl);
            logger.debug('ImageCache', `🎯 BLOB CACHED: ${normalizedUrl} -> ${blobUrl}`);
            resolve(blobUrl);
          } else {
            logger.warn('ImageCache', `Failed to create blob for ${normalizedUrl}`);
            resolve(null);
          }
        }, 'image/jpeg', 0.9);
      } catch (corsError) {
        // This catches the "Tainted canvases may not be exported" error
        logger.warn('ImageCache', `CORS/Tainted canvas error for ${normalizedUrl}:`, corsError.message);
        reject(corsError);
      }
    });
  } catch (error) {
    logger.warn('ImageCache', `Error creating blob for ${normalizedUrl}:`, error.message);
    throw error;
  }
};
